import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-full max-w-xl p-8 flex flex-col space-y-4" }
const _hoisted_2 = { class: "flex flex-row items-start space-x-2" }
const _hoisted_3 = { class: "flex -ml-3" }
const _hoisted_4 = { class: "text-xl font-semibold text-gray-900 self-center" }
const _hoisted_5 = { class: "text-base font-regular leading-normal text-gray-600 pb-2" }
const _hoisted_6 = { class: "w-full justify-start flex flex-col space-y-4" }
const _hoisted_7 = { class: "flex flex-end justify-end space-x-4" }

import { computed, inject, onMounted, PropType, ref } from 'vue';
import axios, { CancelTokenSource } from 'axios';
import { BaseButton } from '@/lib/components/Button';
import { BaseIcon } from '@/lib/components/Icon';
import { BaseSelect } from '@/lib/components/Select';
import { ITranslatedOption, IValidationError } from '@/lib';
import { CopdDischargeReason, CopdMeasurementDischargeParams, CopdOutcome, Patient, Worklist } from '@/models';
import { CopdProgramService } from '@/services/api';
import {
  dischargeReasonsCopdAa,
  selectableDischargeReasons
} from '@/helpers/home-monitoring.helper';
import { HomeMonitoringPathways } from '@/lib/components/Programs/IbdProgram/constants';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { FEATURES } from '@/constants';


export default /*#__PURE__*/_defineComponent({
  __name: 'DischargeProgramModal',
  props: {
  programId: {
    type: String,
    required: true
  },
  status: {
    type: String,
    required: true
  },
  worklist: {
    type: Object as PropType<Worklist>,
    default: () => null
  },
  patient: {
    type: Object as PropType<Patient>,
    default: () => null
  }
},
  emits: ["close", "record-event", "discharged"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const dischargeReasonValue = ref<CopdDischargeReason | null>(null);
const request = ref<CancelTokenSource | null>(null);
const alreadyDischarged = ref(false);
const errors = ref<IValidationError>({
  errors: {},
  message: ''
});
const copdProgramService = new CopdProgramService();
const isCopdAdmissionAvoidanceFeatureEnabled = ref<boolean>(isFeatureFlagEnabled(FEATURES.COPD_ADMISSION_AVOIDANCE));
const dischargeReasons = computed<ITranslatedOption[]>(() => {
  if (isCopdAdmissionAvoidanceFeatureEnabled.value && props.worklist?.slug === HomeMonitoringPathways.CopdAdmissionAvoidance) {
    return dischargeReasonsCopdAa;
  }
  return selectableDischargeReasons();
});

onMounted(() => {
  emit('record-event', 'started discharging patient from pathway');
});

const updatePatient = inject('updatePatient', null);

const dischargePatient = async () => {
  try {
    //   TODO: Measurements list has to be validated with BE
    const dischargeParams: CopdMeasurementDischargeParams = {
      outcome: CopdOutcome.discharge,
      reason: dischargeReasonValue.value,
      measurements: []
    };
    request.value = axios.CancelToken.source();
    await copdProgramService.dischargeProgram(props.programId, dischargeParams, {
      cancelToken: request.value.token
    });

    if (isCopdAdmissionAvoidanceFeatureEnabled.value) {
      const programs = [...props.patient.copd_home_monitoring_latest_programs_across_all_pathways];
      const program = programs.find(({ id }) => id === props.programId);

      if (program) {
        // Active program means discharged_program_review_id = null thus we need to update it with programId
        program.discharged_program_review_id = props.programId;
        updatePatient({ ...props.patient });
      }
    }

    emit('record-event', 'completed discharging patient from pathway');
    emit('close', true);
    emit('discharged', true);
  } catch (e) {
    if (!axios.isCancel(e)) {
      if (!axios.isAxiosError(e)) {
        throw e;
      }
      if (e.response.status === 400) {
        errors.value = { message: e.response.data.message, errors: {} };
        alreadyDischarged.value = true;
      } else {
        errors.value = e.response.data;
      }
    }
  }
};

const updateDischargeReason = (reason: CopdDischargeReason) => {
  dischargeReasonValue.value = reason;
};
const closeDischargeModal = () => {
  dischargeReasonValue.value = null;
  emit('close', true);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BaseIcon), {
          name: "outcome",
          stroke: false,
          height: "2.5rem"
        })
      ]),
      _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('custom.uhb.copd.discharge-patient')), 1)
    ]),
    _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t('custom.uhb.copd.discharge-desc')), 1),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_unref(BaseSelect), {
        "model-value": dischargeReasonValue.value,
        "data-dusk": "discharge-reason",
        error: errors.value?.errors?.reason?.[0],
        label: _ctx.$t('custom.uhb.copd.outcome-reason'),
        options: dischargeReasons.value,
        placeholder: _ctx.$t('custom.uhb.copd.select-reason'),
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (updateDischargeReason($event as CopdDischargeReason)))
      }, null, 8, ["model-value", "error", "label", "options", "placeholder"]),
      (alreadyDischarged.value)
        ? (_openBlock(), _createBlock(_unref(BaseButton), {
            key: 0,
            size: "small",
            color: "ghost",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close', true)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.close')), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(_unref(BaseButton), {
          class: "mt-8",
          size: "large",
          color: "ghost",
          onClick: closeDischargeModal
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('platform.common.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(BaseButton), {
          class: "mt-8",
          size: "large",
          color: "alert",
          "data-dusk": "discharge-patient-button",
          onClick: dischargePatient
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('custom.uhb.copd.discharge-patient')), 1)
          ]),
          _: 1
        })
      ])
    ])
  ]))
}
}

})